import React, { useState, useEffect } from 'react';
import { useToast } from '../../../components/Toast';
import './HighlightsForm.css';

const MAX_IMAGE_SIZE = 5 * 1024 * 1024;
const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

export default function AboutUsPrincipalForm({ apiBase }) {
  const toastUtil = useToast();
  const ToastContainer = toastUtil.ToastContainer;
  const toast = toastUtil;

  const [formData, setFormData] = useState({
    name: '',
    credentials: '',
    role: '',
    opening: '',
    paragraph_one: '',
    paragraph_two: '',
    closing: '',
    theme_line: '',
    principal_photo_id: ''
  });

  const [pendingFields, setPendingFields] = useState({});
  const [originalData, setOriginalData] = useState({...formData});
  const [loading, setLoading] = useState(true);
  const [sectionId, setSectionId] = useState(null);
  const [submitting, setSubmitting] = useState({});
  const [imageUploading, setImageUploading] = useState(false);
  const [imageProgress, setImageProgress] = useState(0);
  const [mediaMap, setMediaMap] = useState({});

  useEffect(() => {
    loadMediaMapAndSection();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiBase]);

  const loadMediaMap = async () => {
    try {
      console.log('[AboutUsPrincipalForm] Fetching media map...');
      const response = await fetch(
        `${apiBase}/aboutus/cms/media?_t=${Date.now()}`,
        { 
          credentials: 'include',
          headers: { 'Cache-Control': 'no-cache' }
        }
      );
      
      if (!response.ok) return;
      
      const result = await response.json();
      if (result.ok && Array.isArray(result.data)) {
        const map = {};
        result.data.forEach((media) => {
          const mediaId = media.id || media.media_id;
          const mediaUrl = media.url || media.file_path;
          
          if (mediaId && mediaUrl) {
            map[mediaId] = mediaUrl;
            map[String(mediaId)] = mediaUrl;
          }
        });
        console.log('[AboutUsPrincipalForm] ✓ Media map ready:', Object.keys(map).length, 'items');
        setMediaMap(map);
      }
    } catch (err) {
      console.error('[AboutUsPrincipalForm] Media fetch error:', err.message);
    }
  };

  const loadMediaMapAndSection = async () => {
    await Promise.all([loadMediaMap(), loadSection()]);
  };

  const loadSection = async () => {
    setLoading(true);
    try {
      console.log('[AboutUsPrincipalForm] Loading principal section...');
      
      let principalSection = null;
      
      // Try admin endpoint first
      console.log('[AboutUsPrincipalForm] Trying authenticated endpoint...');
      const adminResponse = await fetch(
        `${apiBase}/aboutus/cms/sections`,
        { 
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' }
        }
      );
      
      if (adminResponse.ok) {
        const result = await adminResponse.json();
        if (result.ok && Array.isArray(result.data)) {
          principalSection = result.data.find(s => s.section_type === 'about-us-principal-shell');
          console.log('[AboutUsPrincipalForm] ✓ Got data from admin endpoint');
        }
      } else if (adminResponse.status === 401) {
        console.log('[AboutUsPrincipalForm] Admin endpoint unauthorized, trying public endpoint...');
      }
      
      // Fallback to public endpoint
      if (!principalSection) {
        const publicResponse = await fetch(
          `${apiBase}/aboutus/cms/sections?published=true`,
          { 
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' }
          }
        );
        
        if (publicResponse.ok) {
          const result = await publicResponse.json();
          if (result.ok && Array.isArray(result.data)) {
            principalSection = result.data.find(s => s.section_type === 'about-us-principal-shell');
            console.log('[AboutUsPrincipalForm] ✓ Got data from public endpoint');
          }
        }
      }
      
      if (!principalSection) {
        throw new Error('Principal section not found');
      }

      setSectionId(principalSection.id);
      console.log('[AboutUsPrincipalForm] Found principal section:', principalSection.id);

      // Load pending changes
      let pendingFieldsMap = {};
      
      const pendingResponse = await fetch(
        `${apiBase}/cms/section/${principalSection.id}/with-pending`,
        { 
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' }
        }
      );
      
      if (pendingResponse.ok) {
        const pendingResult = await pendingResponse.json();
        
        if (pendingResult.success && pendingResult.data && pendingResult.data.pending_changes) {
          const { pending_changes } = pendingResult.data;

          if (Array.isArray(pending_changes)) {
            pending_changes.forEach(change => {
              if (change.status === 'pending' && change.locale === 'en') {
                pendingFieldsMap[change.field_id] = true;
              }
            });
          }
        }
      }

      if (principalSection.content_published) {
        let published = principalSection.content_published;
        if (typeof published === 'string') {
          published = JSON.parse(published);
        }

        const publishedEn = published.en || {};
        const initialData = {
          name: publishedEn.name || '',
          credentials: publishedEn.credentials || '',
          role: publishedEn.role || '',
          opening: publishedEn.opening || '',
          paragraph_one: publishedEn.paragraph_one || '',
          paragraph_two: publishedEn.paragraph_two || '',
          closing: publishedEn.closing || '',
          theme_line: publishedEn.theme_line || '',
          principal_photo_id: publishedEn.principal_photo_id || ''
        };

        setFormData(initialData);
        setOriginalData(initialData);
        setPendingFields(pendingFieldsMap);
        console.log('[AboutUsPrincipalForm] ✓ Section loaded:', {
          id: principalSection.id,
          pendingFields: pendingFieldsMap
        });
      }
    } catch (err) {
      console.error('[AboutUsPrincipalForm] Load error:', err);
      toast.error('Error loading form: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (fieldName, value) => {
    setFormData(prev => ({
      ...prev,
      [fieldName]: value
    }));
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    console.log('[AboutUsPrincipalForm] Image upload started:', { filename: file.name, size: file.size, type: file.type });

    if (file.size > MAX_IMAGE_SIZE) {
      toast.error(`Max 5MB, got ${(file.size / 1024 / 1024).toFixed(2)}MB`);
      return;
    }

    if (!ALLOWED_TYPES.includes(file.type)) {
      toast.error('Only JPG, PNG, WebP allowed');
      return;
    }

    setImageUploading(true);
    setImageProgress(0);

    try {
      const formDataToUpload = new FormData();
      formDataToUpload.append('file', file);

      const xhr = new XMLHttpRequest();

      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable) {
          const percent = Math.round((e.loaded / e.total) * 100);
          console.log('[AboutUsPrincipalForm] Upload progress:', percent + '%');
          setImageProgress(percent);
        }
      });

      xhr.addEventListener('load', () => {
        console.log('[AboutUsPrincipalForm] Upload completed, status:', xhr.status);
        console.log('[AboutUsPrincipalForm] Response text:', xhr.responseText);
        
        if (xhr.status === 200 || xhr.status === 201) {
          try {
            const response = JSON.parse(xhr.responseText);
            console.log('[AboutUsPrincipalForm] Parsed response:', response);
            
            if (response.success || response.ok) {
              const mediaId = response.media_id || response.id;
              console.log('[AboutUsPrincipalForm] ✓ Image uploaded, media ID:', mediaId);
              
              if (mediaId) {
                handleChange('principal_photo_id', mediaId);
                toast.success('✓ Image uploaded (ID: ' + mediaId + ')');
              } else {
                toast.error('No media ID in response');
              }
            } else {
              toast.error(response.error || 'Upload failed');
            }
          } catch (parseErr) {
            console.error('[AboutUsPrincipalForm] JSON parse error:', parseErr);
            toast.error('Invalid server response');
          }
        } else {
          console.error('[AboutUsPrincipalForm] Upload failed with status', xhr.status);
          toast.error('Upload failed (HTTP ' + xhr.status + ')');
        }
        setImageUploading(false);
      });

      xhr.addEventListener('error', () => {
        console.error('[AboutUsPrincipalForm] XHR error event');
        toast.error('Upload failed - network error');
        setImageUploading(false);
      });

      xhr.addEventListener('abort', () => {
        console.log('[AboutUsPrincipalForm] Upload aborted');
        toast.warning('Upload cancelled');
        setImageUploading(false);
      });

      console.log('[AboutUsPrincipalForm] Opening XHR to:', `${apiBase}/aboutus/cms/media/upload?section_type=about-us-principal-shell`);
      xhr.open('POST', `${apiBase}/aboutus/cms/media/upload?section_type=about-us-principal-shell`);
      xhr.withCredentials = true;
      xhr.send(formDataToUpload);
    } catch (err) {
      console.error('[AboutUsPrincipalForm] Upload error:', err);
      toast.error('Upload error: ' + err.message);
      setImageUploading(false);
    }
  };

  const handleSubmitField = async (fieldName) => {
    if (!sectionId) {
      toast.error('Section ID not loaded');
      return;
    }

    // Check if value changed
    if (JSON.stringify(formData[fieldName]) === JSON.stringify(originalData[fieldName])) {
      toast.warning('No changes to submit');
      return;
    }

    console.log(`[AboutUsPrincipalForm] Submitting ${fieldName}:`, {
      old: originalData[fieldName]?.substring ? originalData[fieldName].substring(0, 50) : originalData[fieldName],
      new: formData[fieldName]?.substring ? formData[fieldName].substring(0, 50) : formData[fieldName]
    });

    setSubmitting(prev => ({ ...prev, [fieldName]: true }));

    try {
      const response = await fetch(
        `${apiBase}/cms/field-change/submit`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            section_id: sectionId,
            field_id: fieldName,
            field_name: fieldName.replace(/_/g, ' '),
            new_value: formData[fieldName],
            old_value: originalData[fieldName],
            locale: 'en'
          })
        }
      );

      const result = await response.json();

      if (result.success) {
        toast.success(`${fieldName} submitted for approval`);
        
        setPendingFields(prev => ({
          ...prev,
          [fieldName]: true
        }));

        setOriginalData(prev => ({
          ...prev,
          [fieldName]: formData[fieldName]
        }));
      } else {
        if (response.status === 409) {
          toast.error('This field already has a pending approval');
        } else {
          toast.error(result.error || result.message || 'Submit failed');
        }
      }
    } catch (err) {
      toast.error('Error: ' + err.message);
    } finally {
      setSubmitting(prev => ({ ...prev, [fieldName]: false }));
    }
  };

  if (loading) {
    return (
      <div className="hero-form-wrapper">
        <div className="loading">
          <div className="loading-spinner"></div>
          <div className="loading-text">Loading Principal Message Section...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="hero-form-wrapper">
      <ToastContainer />

      <div className="hero-form-header">
        <h1 className="hero-form-title">Principal / Head of School Message</h1>
        <p className="hero-form-description">Edit and submit each field individually</p>
      </div>

      <div className="form-section">
        <div className="form-field-card">
          <div className="field-header">
            <label>Name</label>
            {pendingFields.name && <span className="status-badge pending">Pending</span>}
          </div>
          <input
            type="text"
            value={formData.name}
            onChange={(e) => handleChange('name', e.target.value)}
            disabled={submitting.name || pendingFields.name}
            placeholder="e.g., Dr. Amelia Hartono"
            className="text-input"
          />
          <button
            onClick={() => handleSubmitField('name')}
            disabled={submitting.name || pendingFields.name || formData.name === originalData.name}
            className="submit-btn"
          >
            {submitting.name ? 'Submitting...' : 'Submit For Approval'}
          </button>
        </div>

        {/* Credentials */}
        <div className="form-field-card">
          <div className="field-header">
            <label>Credentials</label>
            {pendingFields.credentials && <span className="status-badge pending">Pending</span>}
          </div>
          <input
            type="text"
            value={formData.credentials}
            onChange={(e) => handleChange('credentials', e.target.value)}
            disabled={submitting.credentials || pendingFields.credentials}
            placeholder="e.g., M.Ed., Ed.D."
            className="text-input"
          />
          <button
            onClick={() => handleSubmitField('credentials')}
            disabled={submitting.credentials || pendingFields.credentials || formData.credentials === originalData.credentials}
            className="submit-btn"
          >
            {submitting.credentials ? 'Submitting...' : 'Submit For Approval'}
          </button>
        </div>

        {/* Role */}
        <div className="form-field-card">
          <div className="field-header">
            <label>Role</label>
            {pendingFields.role && <span className="status-badge pending">Pending</span>}
          </div>
          <input
            type="text"
            value={formData.role}
            onChange={(e) => handleChange('role', e.target.value)}
            disabled={submitting.role || pendingFields.role}
            placeholder="e.g., Principal / Head of School"
            className="text-input"
          />
          <button
            onClick={() => handleSubmitField('role')}
            disabled={submitting.role || pendingFields.role || formData.role === originalData.role}
            className="submit-btn"
          >
            {submitting.role ? 'Submitting...' : 'Submit For Approval'}
          </button>
        </div>

        {/* Opening */}
        <div className="form-field-card">
          <div className="field-header">
            <label>Opening</label>
            {pendingFields.opening && <span className="status-badge pending">Pending</span>}
          </div>
          <textarea
            value={formData.opening}
            onChange={(e) => handleChange('opening', e.target.value)}
            disabled={submitting.opening || pendingFields.opening}
            placeholder="Opening message..."
            className="textarea-input"
            rows="3"
          />
          <button
            onClick={() => handleSubmitField('opening')}
            disabled={submitting.opening || pendingFields.opening || formData.opening === originalData.opening}
            className="submit-btn"
          >
            {submitting.opening ? 'Submitting...' : 'Submit For Approval'}
          </button>
        </div>

        {/* Paragraph One */}
        <div className="form-field-card">
          <div className="field-header">
            <label>First Paragraph</label>
            {pendingFields.paragraph_one && <span className="status-badge pending">Pending</span>}
          </div>
          <textarea
            value={formData.paragraph_one}
            onChange={(e) => handleChange('paragraph_one', e.target.value)}
            disabled={submitting.paragraph_one || pendingFields.paragraph_one}
            placeholder="First paragraph..."
            className="textarea-input"
            rows="4"
          />
          <button
            onClick={() => handleSubmitField('paragraph_one')}
            disabled={submitting.paragraph_one || pendingFields.paragraph_one || formData.paragraph_one === originalData.paragraph_one}
            className="submit-btn"
          >
            {submitting.paragraph_one ? 'Submitting...' : 'Submit For Approval'}
          </button>
        </div>

        {/* Paragraph Two */}
        <div className="form-field-card">
          <div className="field-header">
            <label>Second Paragraph</label>
            {pendingFields.paragraph_two && <span className="status-badge pending">Pending</span>}
          </div>
          <textarea
            value={formData.paragraph_two}
            onChange={(e) => handleChange('paragraph_two', e.target.value)}
            disabled={submitting.paragraph_two || pendingFields.paragraph_two}
            placeholder="Second paragraph..."
            className="textarea-input"
            rows="4"
          />
          <button
            onClick={() => handleSubmitField('paragraph_two')}
            disabled={submitting.paragraph_two || pendingFields.paragraph_two || formData.paragraph_two === originalData.paragraph_two}
            className="submit-btn"
          >
            {submitting.paragraph_two ? 'Submitting...' : 'Submit For Approval'}
          </button>
        </div>

        {/* Closing */}
        <div className="form-field-card">
          <div className="field-header">
            <label>Closing</label>
            {pendingFields.closing && <span className="status-badge pending">Pending</span>}
          </div>
          <textarea
            value={formData.closing}
            onChange={(e) => handleChange('closing', e.target.value)}
            disabled={submitting.closing || pendingFields.closing}
            placeholder="Closing message..."
            className="textarea-input"
            rows="3"
          />
          <button
            onClick={() => handleSubmitField('closing')}
            disabled={submitting.closing || pendingFields.closing || formData.closing === originalData.closing}
            className="submit-btn"
          >
            {submitting.closing ? 'Submitting...' : 'Submit For Approval'}
          </button>
        </div>

        {/* Theme Line */}
        <div className="form-field-card">
          <div className="field-header">
            <label>Theme Line</label>
            {pendingFields.theme_line && <span className="status-badge pending">Pending</span>}
          </div>
          <input
            type="text"
            value={formData.theme_line}
            onChange={(e) => handleChange('theme_line', e.target.value)}
            disabled={submitting.theme_line || pendingFields.theme_line}
            placeholder="e.g., Nurturing Character, Inspiring Excellence, Building Futures."
            className="text-input"
          />
          <button
            onClick={() => handleSubmitField('theme_line')}
            disabled={submitting.theme_line || pendingFields.theme_line || formData.theme_line === originalData.theme_line}
            className="submit-btn"
          >
            {submitting.theme_line ? 'Submitting...' : 'Submit For Approval'}
          </button>
        </div>

        {/* Photo */}
        <div className="form-field-card">
          <div className="field-header">
            <label>Principal Photo</label>
            {pendingFields.principal_photo_id && <span className="status-badge pending">Pending</span>}
          </div>
          
          {/* Photo Preview Card */}
          {formData.principal_photo_id && (
            <div style={{
              display: 'flex',
              gap: '16px',
              marginBottom: '16px',
              padding: '12px',
              backgroundColor: '#f9fafb',
              borderRadius: '8px',
              border: '1px solid #e5e7eb'
            }}>
              {/* Photo Thumbnail */}
              {mediaMap[formData.principal_photo_id] ? (
                <div style={{
                  flex: '0 0 120px',
                  height: '120px',
                  borderRadius: '6px',
                  overflow: 'hidden',
                  backgroundColor: '#f3f4f6',
                  border: '1px solid #d1d5db',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <img 
                    src={mediaMap[formData.principal_photo_id]} 
                    alt="Principal" 
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover'
                    }}
                  />
                </div>
              ) : (
                <div style={{
                  flex: '0 0 120px',
                  height: '120px',
                  borderRadius: '6px',
                  backgroundColor: '#fee2e2',
                  border: '1px solid #fca5a5',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#991b1b',
                  fontSize: '12px',
                  textAlign: 'center',
                  padding: '8px'
                }}>
                  Photo not found (ID: {formData.principal_photo_id})
                </div>
              )}
              
              {/* Photo Info */}
              <div style={{ flex: 1 }}>
                <div style={{ 
                  marginBottom: '8px',
                  fontSize: '13px',
                  color: '#6b7280'
                }}>
                  <strong>Current Photo</strong>
                </div>
                <div style={{ 
                  marginBottom: '8px',
                  fontSize: '12px',
                  color: '#6b7280'
                }}>
                  Media ID: <code style={{ backgroundColor: '#f3f4f6', padding: '2px 6px', borderRadius: '4px' }}>{formData.principal_photo_id}</code>
                </div>
                {mediaMap[formData.principal_photo_id] && (
                  <div style={{ 
                    fontSize: '12px',
                    color: '#10b981'
                  }}>
                    ✓ Photo loaded successfully
                  </div>
                )}
                {!mediaMap[formData.principal_photo_id] && (
                  <div style={{ 
                    fontSize: '12px',
                    color: '#ef4444'
                  }}>
                    ✗ Photo URL not found in media map
                  </div>
                )}
              </div>
            </div>
          )}
          
          {/* Upload Section */}
          <div style={{ marginBottom: '12px' }}>
            <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '8px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              {formData.principal_photo_id ? 'Replace Photo' : 'Upload Photo'}
            </div>
            <div className="modal-upload-area">
              <input
                type="file"
                accept="image/jpeg,image/png,image/webp"
                onChange={handleImageUpload}
                disabled={imageUploading || pendingFields.principal_photo_id}
                className="modal-file-input"
                id="principal-photo"
              />
              <label
                htmlFor="principal-photo"
                className="modal-upload-label"
              >
                <span className="upload-icon-new">📤</span>
                <span>
                  {imageUploading ? `Uploading ${imageProgress}%...` : 'Click to upload image'}
                </span>
              </label>
            </div>
            <span className="hint">Max 5MB • Recommended: 220x294px</span>
          </div>
          
          <button
            onClick={() => handleSubmitField('principal_photo_id')}
            disabled={submitting.principal_photo_id || pendingFields.principal_photo_id || formData.principal_photo_id === originalData.principal_photo_id || !formData.principal_photo_id}
            className="submit-btn"
          >
            {submitting.principal_photo_id ? 'Submitting...' : 'Submit For Approval'}
          </button>
        </div>
      </div>
    </div>
  );
}
